import React from 'react';
import { UserManager, type User } from 'oidc-client-ts';
import { UserProfile } from '../../types';
import AppConfig from '../../AppConfig.ts';

interface UserContextType {
  userProfile: UserProfile | null;
  apiAccessToken: string;
  isLoading: boolean;
  isLoggingOut: boolean;
}

const UserContext = React.createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a OidcProvider');
  }
  return context;
};

// These values are grouped here for convenience, but can be moved in favor of cleaner code
const client_id = 'compliance-education'; //TODO: Update the client id after registering your API client with OIDC service
export const OIDC_CALLBACK_PATH = '/Login/Callback'; // OIDC callback path

/** Session key for deep-link restoration after OIDC redirect (see handleCallback). */
const POST_LOGIN_REDIRECT_STORAGE_KEY = 'compliance-portal-post-login-redirect';

/** Synchronous guard — React `isLoggingOut` can lag behind `removeUser()`. */
let suppressPostLoginRedirectSave = false;

export function clearPostLoginRedirect(): void {
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
}

/** Call before sign-out so the next login lands on the role-based home, not the last editor URL. */
export function beginLogoutSession(): void {
  suppressPostLoginRedirectSave = true;
  clearPostLoginRedirect();
}

export function endLogoutSession(): void {
  suppressPostLoginRedirectSave = false;
}

/** Save current path+query before signinRedirect so handleCallback can return here instead of `/`. */
export function savePostLoginRedirectFromCurrentLocation(): void {
  if (suppressPostLoginRedirectSave) {
    return;
  }

  const path = `${window.location.pathname}${window.location.search}`;
  if (!path.startsWith('/') || path.startsWith('//')) return;
  if (path.startsWith(OIDC_CALLBACK_PATH)) return;
  if (path.startsWith('/logout')) return;
  if (path.includes('silent-callback')) return;
  sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, path);
}

function consumePostLoginRedirectOrHome(): string {
  const raw = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
}

const userManager = new UserManager({
  authority: AppConfig.oidcClientUrl,
  client_id,
  redirect_uri: `${window.location.origin}${OIDC_CALLBACK_PATH}`,
  post_logout_redirect_uri: window.location.origin, // Redirect to home after logout
  response_type: 'code',
  scope: 'openid email profile membership', // Standard OIDC scopes + platform membership
  automaticSilentRenew: true, // Automatically refresh tokens before expiration
  silent_redirect_uri: `${window.location.origin}/silent-callback.html`, // Match OIDC registration
  accessTokenExpiringNotificationTimeInSeconds: 60, // Notify 60 seconds before expiration
  // Note: resource parameter removed - add back when backend API is available
});

// Export userManager for manual operations (like sign out)
export { userManager };

export default function OidcProvider({ children }: { children: React.ReactNode }): JSX.Element | null {
  const [apiAccessToken, setApiAccessToken] = React.useState<string>('');
  const [userProfile, setUserProfile] = React.useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLoggingOut, setIsLoggingOut] = React.useState<boolean>(false);

  const loadUser = React.useCallback(async (user: User | null): Promise<void> => {
    if (user && !user.expired) {
      endLogoutSession();
      setIsLoggingOut(false); // Clear logout flag when user logs in
      setApiAccessToken(user.id_token ?? '');
      setUserProfile(user.profile as UserProfile);
    } else {
      setApiAccessToken('');
      setUserProfile(null);
    }
  }, []);

  const handleCallback = React.useCallback(async (): Promise<void> => {
    try {
      console.log('[OIDC Callback] Starting callback processing');
      
      // Clear any existing user data before processing new callback
      // This prevents stale data when switching accounts
      await userManager.removeUser();
      console.log('[OIDC Callback] Cleared previous session data');
      
      const user = await userManager.signinRedirectCallback();
      console.log('[OIDC Callback] Received user from OIDC:', {
        email: user.profile.email,
        sub: user.profile.sub,
        tokenIssuedAt: new Date((user.profile as any).iat * 1000).toISOString()
      });
      
      // Decode and log the actual token to verify it matches
      if (user.id_token) {
        const tokenPayload = JSON.parse(atob(user.id_token.split('.')[1]));
        console.log('[OIDC Callback] Token contents:', {
          email: tokenPayload.email,
          sub: tokenPayload.sub,
          issuedAt: new Date(tokenPayload.iat * 1000).toISOString()
        });
      }
      
      await loadUser(user);

      const returnTo = consumePostLoginRedirectOrHome();
      window.location.replace(returnTo);
    } catch (error) {
      console.error('[OIDC Callback] Error processing callback:', error);
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
      window.location.replace('/');
    }
  }, [loadUser]);

  const initializeAuth = React.useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);

      // Check if we're on the callback path
      if (window.location.pathname === OIDC_CALLBACK_PATH) {
        await handleCallback();
        return;
      }

      // Check if we're being launched from the home page
      // The home page authenticates centrally, so we should sync with that session
      const referrer = document.referrer;
      const isFromHomePage = referrer.includes('home.diligentoneplatform') || referrer.includes('home.highbond');
      
      if (isFromHomePage) {
        console.log('[OIDC] Launched from home page, syncing authentication state');
        
        // Get the current local user
        const localUser = await userManager.getUser();
        
        if (localUser && localUser.id_token) {
          // Decode local token to check which user it belongs to
          const localTokenPayload = JSON.parse(atob(localUser.id_token.split('.')[1]));
          console.log('[OIDC] Local token belongs to:', {
            email: localTokenPayload.email,
            sub: localTokenPayload.sub,
            issuedAt: new Date(localTokenPayload.iat * 1000).toISOString()
          });
        }
        
        try {
          // Clear any existing local session
          await userManager.removeUser();
          console.log('[OIDC] Cleared local session, fetching current platform session');
          
          // Use signinSilent to get the current OIDC session without redirect
          // This will use the session from home page login
          const freshUser = await userManager.signinSilent();
          
          if (freshUser) {
            console.log('[OIDC] Retrieved fresh session:', {
              email: freshUser.profile.email,
              sub: freshUser.profile.sub
            });
            
            await loadUser(freshUser);
            setIsLoading(false);
            return;
          } else {
            console.warn('[OIDC] Silent auth returned null, falling back to redirect');
            savePostLoginRedirectFromCurrentLocation();
            await userManager.signinRedirect();
            return;
          }
        } catch (silentError) {
          console.warn('[OIDC] Silent auth failed, falling back to redirect:', silentError);
          savePostLoginRedirectFromCurrentLocation();
          await userManager.signinRedirect();
          return;
        }
      }

      // Try to get existing user from storage
      const user = await userManager.getUser();

      if (user && !user.expired) {
        // User is already authenticated locally
        await loadUser(user);
      } else {
        // No valid local user - clear expired session if needed
        if (user && user.expired) {
          await userManager.removeUser();
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error initializing authentication:', error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [handleCallback, loadUser]);

  // Setup UserManager event listeners
  React.useEffect(() => {
    const handleAccessTokenExpiring = () => {
      // Silent renewal will happen automatically via automaticSilentRenew setting
    };

    const handleAccessTokenExpired = () => {
      // Token expired - clear user state
      setUserProfile(null);
      setApiAccessToken('');
    };

    const handleSilentRenewError = (error: Error) => {
      console.error('Silent token renewal failed:', error);
      setUserProfile(null);
      setApiAccessToken('');
    };

    const handleUserLoaded = (user: User) => {
      // User loaded or renewed - update state
      loadUser(user);
    };

    const handleUserUnloaded = () => {
      // User removed - set logout flag to prevent auto-login
      setIsLoggingOut(true);
      setUserProfile(null);
      setApiAccessToken('');
    };

    // Listen for front-channel logout notifications via BroadcastChannel
    let logoutChannel: BroadcastChannel | null = null;
    
    const handleBroadcastLogout = (event: MessageEvent) => {
      if (event.data?.type === 'frontchannel-logout') {
        console.log('[OIDC] Front-channel logout notification received via BroadcastChannel');
        handleLogout();
      }
    };
    
    const handleStorageLogout = (event: StorageEvent) => {
      // Detect logout trigger from other tabs via localStorage event
      if (event.key === 'oidc-logout-trigger') {
        console.log('[OIDC] Front-channel logout notification received via localStorage event');
        handleLogout();
      }
    };
    
    const handleLogout = () => {
      console.log('[OIDC] ✅ Front-channel logout processed');
      beginLogoutSession();
      // User logged out from another app/tab in the platform
      // Clear local state and set logout flag
      setIsLoggingOut(true);
      setUserProfile(null);
      setApiAccessToken('');
      
      // Optionally: Clear any remaining OIDC data
      userManager.removeUser().catch(err => {
        console.warn('[OIDC] Error removing user during front-channel logout:', err);
      });
    };

    // Register event handlers
    userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    userManager.events.addSilentRenewError(handleSilentRenewError);
    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);

    // Listen for front-channel logout via BroadcastChannel (modern browsers)
    try {
      logoutChannel = new BroadcastChannel('oidc-logout');
      logoutChannel.addEventListener('message', handleBroadcastLogout);
      console.log('[OIDC] BroadcastChannel listener registered');
    } catch (e) {
      console.warn('[OIDC] BroadcastChannel not supported:', e);
    }
    
    // Fallback: Listen for logout via storage events (works in all browsers)
    window.addEventListener('storage', handleStorageLogout);

    // Initialize authentication
    initializeAuth();

    // Cleanup event listeners on unmount
    return () => {
      userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      
      // Clean up BroadcastChannel
      if (logoutChannel) {
        logoutChannel.removeEventListener('message', handleBroadcastLogout);
        logoutChannel.close();
      }
      
      // Clean up storage event listener
      window.removeEventListener('storage', handleStorageLogout);
    };
  }, [initializeAuth, loadUser]);

  return (
    <UserContext.Provider value={{ userProfile, apiAccessToken, isLoading, isLoggingOut }}>
      {children}
    </UserContext.Provider>
  );
}
